import { createSelector } from "reselect"
import { get, chain, pick, map, keyBy, find, includes } from "lodash"
import { COLORS } from "helpers/analyzeConstants"
import { buildContributorKey } from "helpers/contributors"
import { getSortedContentSets } from "selectors/contentSets"

const EMPTY_OBJECT = {}
const EMPTY_ARRAY = []

const getDecompositionType = state => get(state, "modules.rebalance.decompositionType")

export const getExposureSummary = state => get(state, "global.data.exposure.data.model.simulation.exposure.0", EMPTY_ARRAY)
export const getRebalancedExposureSummary = state => get(state, "modules.rebalance.rebalancedExposure", EMPTY_ARRAY)

export const getContentSetSummary = state => get(state, "global.data.contentSetExposure.data.model.simulation", EMPTY_OBJECT)
export const getRebalancedContentSetSummary = state => get(state, "modules.rebalance.rebalancedContentSetExposure", EMPTY_ARRAY)

// Contributors
const getRawExposureContributors = state => get(state, "global.data.exposureContributors.data.model.simulation.exposureContributors.0.contributors", EMPTY_ARRAY)
const getRawContentSetContributors = state => get(state, "global.data.contentSetContributors.data.model.simulation", EMPTY_OBJECT)

const getRawRebalancedExposureContributors = state => get(state, "modules.rebalance.rebalancedExposureContributors", EMPTY_ARRAY)
const getRawRebalancedContentSetContributors = state => get(state, "modules.rebalance.rebalancedContentSetContributors", EMPTY_OBJECT)

// Grouped Contributors
const getRawGroupedExposureContributors = state => get(state, "global.data.groupedExposureContributors.data.model.simulation.groupedExposureContributors.0.groupedContributors", EMPTY_ARRAY)
const getRawGroupedContentSetContributors = state => get(state, "global.data.groupedContentSetContributors.data.model.simulation", EMPTY_OBJECT)

const getRawGroupedRebalancedExposureContributors = state => get(state, "modules.rebalance.rebalancedGroupedExposureContributors", EMPTY_ARRAY)
const getRawGroupedRebalancedContentSetContributors = state => get(state, "modules.rebalance.rebalancedGroupedContentSetContributors", EMPTY_OBJECT)

export const getSelectedContentSet = createSelector(
  [getDecompositionType, getSortedContentSets],
  (decompositionType, contentSets) => {
    if (includes(["risk", "exposure", "beta"], decompositionType)) {
      return undefined
    }
    return find(contentSets, { escapedId: decompositionType })
  },
)

const getSelectedContentSetEscapedId = createSelector(
  [getSelectedContentSet],
  (contentSet) => get(contentSet, "escapedId"),
)

export const getContentSetCategories = createSelector(
  [getSortedContentSets],
  (contentSets) => chain(contentSets)
    .map(({ escapedId: contentSetId, categories }) => map(categories, ({ id, name }, i) => ({
      id,
      label: name,
      contentSetId,
      color: get(COLORS, i, COLORS[0]),
    })))
    .flatten()
    .value(),
)

export const getContentSetFactors = createSelector(
  [getSortedContentSets],
  (contentSets) => chain(contentSets)
    .map((contentSet) => pick(contentSet, ["factors", "escapedId"]))
    .keyBy("escapedId")
    .mapValues(({ factors }) => map(factors, ({ name, category, id }) => ({ label: name, category, id })))
    .value(),
)

export const getContentSetContributors = createSelector(
  [getRawContentSetContributors, getSelectedContentSetEscapedId],
  (contentSetContributors, contentSetEscapedId) => computeContentSetContributors(get(contentSetContributors, [contentSetEscapedId, 0, "contributors"], EMPTY_ARRAY), contentSetEscapedId),
)

export const getGroupedContentSetContributors = createSelector(
  [getRawGroupedContentSetContributors, getSelectedContentSetEscapedId],
  (groupedContentSetContributors, contentSetEscapedId) => computeContentSetContributors(get(groupedContentSetContributors, [`${contentSetEscapedId}_grouped`, 0, "groupedContributors"], EMPTY_ARRAY), contentSetEscapedId),
)

export const getRebalancedContentSetContributors = createSelector(
  [getRawRebalancedContentSetContributors, getSelectedContentSetEscapedId],
  (contentSetContributors, contentSetEscapedId) => computeContentSetContributors(get(contentSetContributors, [contentSetEscapedId, 0, "contributors"], EMPTY_ARRAY), contentSetEscapedId),
)

export const getGroupedRebalancedContentSetContributors = createSelector(
  [getRawGroupedRebalancedContentSetContributors, getSelectedContentSetEscapedId],
  (groupedContentSetContributors, contentSetEscapedId) => computeContentSetContributors(get(groupedContentSetContributors, [`${contentSetEscapedId}_grouped`, 0, "groupedContributors"], EMPTY_ARRAY), contentSetEscapedId),
)

export const getExposureContributors = createSelector(
  [getRawExposureContributors],
  computeExposureContributors,
)

export const getGroupedExposureContributors = createSelector(
  [getRawGroupedExposureContributors],
  computeExposureContributors,
)

export const getRebalancedExposureContributors = createSelector(
  [getRawRebalancedExposureContributors],
  computeExposureContributors,
)

export const getGroupedRebalancedExposureContributors = createSelector(
  [getRawGroupedRebalancedExposureContributors],
  computeExposureContributors,
)

function computeContentSetContributors(contentSetContributors, contentSetId) {
  return chain(contentSetContributors)
    .keyBy(buildContributorKey)
    .mapValues((contributor) => ({
      [contentSetId]: { factors: keyBy(contributor.factors, "id") },
      ...(contributor.contributors && { contributors: computeContentSetContributors(contributor.contributors, contentSetId) }),
    }))
    .value()
}

function computeExposureContributors(exposureContributors) {
  return chain(exposureContributors)
    .keyBy(buildContributorKey)
    .mapValues((contributor) => ({
      factors: keyBy(contributor.factors, "id"),
      ...(contributor.contributors && { contributors: computeExposureContributors(contributor.contributors) }),
    }))
    .value()
}
